import React, { useState, useEffect } from 'react';
import { 
  FileText, 
  BookOpen, 
  TrendingUp, 
  Users, 
  Calendar,
  Tag,
  Search,
  Globe,
  Mail,
  Share2,
  Heart,
  MessageCircle,
  Eye,
  HelpCircle
} from 'lucide-react';

// 📝 ACCTOO BLOG APP
// This is a separate app running on port 3002
// Maps existing blog data and content marketing features

const App = () => {
  const [activeTab, setActiveTab] = useState('home');
  const [searchQuery, setSearchQuery] = useState('');
  const [isDark, setIsDark] = useState(false);
  const [faqOpenIndex, setFaqOpenIndex] = useState(0);

  useEffect(() => {
    const root = document.documentElement;
    if (isDark) {
      root.classList.add('dark');
    } else {
      root.classList.remove('dark');
    }
  }, [isDark]);

  // Mock blog data based on existing structure from README
  const mockBlogData = {
    categories: [
      { id: 'accounting-tips', name: 'Accounting Tips', count: 15, color: 'blue' },
      { id: 'case-studies', name: 'Case Studies', count: 8, color: 'green' },
      { id: 'industry-news', name: 'Industry News', count: 12, color: 'purple' },
      { id: 'company-updates', name: 'Company Updates', count: 6, color: 'orange' },
      { id: 'tutorials', name: 'Tutorials', count: 10, color: 'red' }
    ],
    featuredPosts: [
      {
        id: 'post_1',
        title: '10 Essential Accounting Tips for Small Businesses in 2024',
        excerpt: 'Learn the fundamental accounting practices that every small business owner should know to maintain financial health and compliance.',
        category: 'accounting-tips',
        author: 'ACCTOO Team',
        publishDate: '2024-01-15',
        readTime: '5 min read',
        views: 1250,
        likes: 89,
        comments: 23,
        featured: true,
        tags: ['small-business', 'accounting-basics', 'compliance']
      },
      {
        id: 'post_2',
        title: 'How Tech Solutions Ltd Increased Revenue by 40% with ACCTOO',
        excerpt: 'A detailed case study showing how implementing ACCTOO helped a mid-sized company streamline their accounting processes.',
        category: 'case-studies',
        author: 'Priya Sharma',
        publishDate: '2024-01-12',
        readTime: '8 min read',
        views: 890,
        likes: 67,
        comments: 15,
        featured: true,
        tags: ['case-study', 'revenue-growth', 'implementation']
      },
      {
        id: 'post_3',
        title: 'New GST Regulations 2024: What Accountants Need to Know',
        excerpt: 'Stay updated with the latest GST changes and how they affect your accounting practices and client services.',
        category: 'industry-news',
        author: 'Rahul Kumar',
        publishDate: '2024-01-10',
        readTime: '6 min read',
        views: 2100,
        likes: 156,
        comments: 42,
        featured: true,
        tags: ['gst', 'regulations', 'compliance', '2024']
      }
    ],
    recentPosts: [
      {
        id: 'post_4',
        title: 'Step-by-Step Guide to Setting Up Chart of Accounts',
        excerpt: 'A comprehensive tutorial for setting up a proper chart of accounts for your business.',
        category: 'tutorials',
        author: 'Amit Patel',
        publishDate: '2024-01-08',
        readTime: '7 min read',
        views: 650,
        likes: 45,
        comments: 12
      },
      {
        id: 'post_5',
        title: 'ACCTOO Mobile App: New Features Released',
        excerpt: 'Discover the latest mobile app features that make accounting on-the-go easier than ever.',
        category: 'company-updates',
        author: 'ACCTOO Team',
        publishDate: '2024-01-05',
        readTime: '4 min read',
        views: 780,
        likes: 52,
        comments: 8
      }
    ],
    stats: {
      totalPosts: 51,
      totalViews: 125000,
      totalLikes: 8900,
      totalComments: 2100,
      monthlyReaders: 15000,
      emailSubscribers: 3200
    },
    faqs: [
      {
        q: 'ACCTOO software kya hai?',
        a: 'ACCTOO ek modern, cloud‑based accounting platform hai jo billing, expenses, GST compliance, reporting aur team collaboration ko simplify karta hai. Ye web browser me chalta hai—install karne ki zarurat nahi.'
      },
      {
        q: 'Start kaise karun? Koi trial/demo milega?',
        a: 'Get Started button se account banaiye, company details add kijiye, team members invite kijiye. Demo data ke saath guided onboarding milta hai. Chahein to CSV se masters (customers, items) import karke 10‑15 minute me go‑live ho sakte hain.'
      },
      {
        q: 'System requirements kya hain?',
        a: 'Latest Chrome/Edge/Firefox ya Safari recommended. Stable internet connection aur desktop/laptop preferred. Mobile view responsive hai, isliye phone par bhi most tasks ho jate hain.'
      },
      {
        q: 'Meri data security kaise ensure hoti hai?',
        a: 'Data Google Cloud (Firebase) par store hota hai. In‑transit TLS 1.2+ encryption, at‑rest AES‑256 encryption, role‑based access control, audit trails aur least‑privilege policies enabled hote hain. 2‑factor auth aur SSO support optional hai.'
      },
      {
        q: 'Data kis region me store hota hai?',
        a: 'Default hosting Google Cloud/Firebase par hoti hai. Setup ke time preferred region (e.g., asia‑south1 for India) select kiya ja sakta hai, jisse latency kam aur data residency needs puri hoti hain.'
      },
      {
        q: 'Backups aur disaster recovery?',
        a: 'Automated nightly backups maintain kiye jate hain aur rolling retention hoti hai (generally 30 din). Point‑in‑time restore request par available hai. Major outages ke case me RTO/RPO business hours ke andar target kiya jata hai.'
      },
      {
        q: 'Integrations kaise hoti hain? Kya API milta hai?',
        a: 'ACCTOO REST APIs, webhooks, aur SSO (OAuth 2.0/JWT) support karta hai. Common ERPs/CRMs ke saath connectors available hain. Custom integration ke liye documented endpoints aur sample code provide kiya jata hai.'
      },
      {
        q: 'Sales documents: Invoice, Challan, Quotation available hain?',
        a: 'Haan. Sales module me teenon flows supported hain—Invoice (standard billing), Challan (delivery/return documentation) aur Quotation (price offer). A4/A5 print templates ready hain aur numbering FY‑wise prefix pattern (INV/CHA/QUO) follow karta hai.'
      },
      {
        q: 'Quick Item Entry aur Party‑wise Quick Items kaise kaam karte hain?',
        a: 'System aapke historical sales ko analyze karke most‑used items ki frequency list banata hai. Sales/Purchase screen par Quick Items buttons se 1‑click me items add ho jate hain. Agar koi party select ho, to party‑specific favorites list dikhti hai jisse billing 2‑3x fast ho jati hai.'
      },
      {
        q: 'MPIN se page/access restriction kaise hoti hai?',
        a: 'Sensitive pages (jaise payments, settings, exports, company switch) MPIN verification ke bina open nahi hote. Role‑based access ke saath MPIN ek extra gate ki tarah act karta hai—maker‑checker approvals aur audit logs ke saath.'
      },
      {
        q: 'GST compliance features kya milte hain?',
        a: 'GST‑ready invoicing, HSN/SAC handling, input tax credit tracking, aur standard reports (jaise GSTR‑1, 3B) generate kiye ja sakte hain. E‑invoice/e‑waybill workflows ke liye integration options available hain.'
      },
      {
        q: 'Multi‑user roles & approvals?',
        a: 'Granular roles (Admin, Manager, Accountant, Viewer) ke saath permissions control milta hai. Sensitive actions ke liye maker‑checker approvals aur full audit logs available hain.'
      },
      {
        q: 'Data export/import options?',
        a: 'Customers, items, vouchers aur reports ko CSV/Excel me export kiya ja sakta hai. Bulk import templates diye jate hain. Programmatic needs ke liye API based import/export bhi possible hai.'
      },
      {
        q: 'Pricing & billing kaise hota hai?',
        a: 'Subscription model per company + user slabs par based hota hai (monthly/annual). Large teams ke liye volume discounts aur invoicing options available hote hain. Exact quote ke liye sales se baat karein.'
      },
      {
        q: 'Support & SLA?',
        a: 'Email/chat support business hours me available hai. Critical incidents ke liye prioritized response aur status updates milte hain. Product guide aur tutorials in‑app embedded hote hain.'
      }
    ]
  };

  const tabs = [
    { id: 'home', name: 'Home', icon: Globe },
    { id: 'categories', name: 'Categories', icon: Tag },
    { id: 'case-studies', name: 'Case Studies', icon: TrendingUp },
    { id: 'tutorials', name: 'Tutorials', icon: BookOpen },
    { id: 'news', name: 'Industry News', icon: FileText },
    { id: 'faq', name: 'FAQ', icon: HelpCircle },
    { id: 'about', name: 'About', icon: Users }
  ];

  const filteredFeatured = mockBlogData.featuredPosts.filter((p) => {
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return (
      p.title.toLowerCase().includes(q) ||
      p.excerpt.toLowerCase().includes(q) ||
      p.author.toLowerCase().includes(q) ||
      p.category.toLowerCase().includes(q) ||
      (p.tags || []).some((t) => t.toLowerCase().includes(q))
    );
  });

  const filteredRecent = mockBlogData.recentPosts.filter((p) => {
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return (
      p.title.toLowerCase().includes(q) ||
      p.excerpt.toLowerCase().includes(q) ||
      p.author.toLowerCase().includes(q) ||
      p.category.toLowerCase().includes(q)
    );
  });

  const renderHome = () => (
    <div className="space-y-8">
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white p-8 rounded-lg">
        <h1 className="text-4xl font-bold mb-4">ACCTOO Blog</h1>
        <p className="text-xl mb-6">Content marketing and thought leadership platform for accounting professionals</p>
        <div className="flex items-center space-x-6">
          <div className="text-center">
            <div className="text-2xl font-bold">{mockBlogData.stats.totalPosts}</div>
            <div className="text-sm">Total Posts</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold">{mockBlogData.stats.monthlyReaders.toLocaleString()}</div>
            <div className="text-sm">Monthly Readers</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold">{mockBlogData.stats.emailSubscribers.toLocaleString()}</div>
            <div className="text-sm">Email Subscribers</div>
          </div>
        </div>
      </div>

      {/* Featured Posts */}
      <div>
        <h2 className="text-2xl font-bold text-gray-900 mb-6">Featured Posts</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredFeatured.map((post) => (
            <div key={post.id} className="card overflow-hidden transition-transform hover:-translate-y-1 hover:shadow-lg">
              <div className="p-6">
                <div className="flex items-center mb-3">
                  <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                    post.category === 'accounting-tips' ? 'bg-blue-100 text-blue-800' :
                    post.category === 'case-studies' ? 'bg-green-100 text-green-800' :
                    post.category === 'industry-news' ? 'bg-purple-100 text-purple-800' :
                    post.category === 'company-updates' ? 'bg-orange-100 text-orange-800' :
                    'bg-red-100 text-red-800'
                  }`}>
                    {post.category.replace('-', ' ')}
                  </span>
                  <span className="ml-2 text-xs text-gray-500">{post.readTime}</span>
                </div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">{post.title}</h3>
                <p className="text-gray-600 dark:text-gray-300 text-sm mb-4">{post.excerpt}</p>
                <div className="flex items-center justify-between text-sm text-gray-500">
                  <span>{post.author}</span>
                  <span>{new Date(post.publishDate).toLocaleDateString()}</span>
                </div>
                <div className="flex items-center space-x-4 mt-4 pt-4 border-t">
                  <div className="flex items-center text-gray-500">
                    <Eye className="h-4 w-4 mr-1" />
                    <span className="text-xs">{post.views}</span>
                  </div>
                  <div className="flex items-center text-gray-500">
                    <Heart className="h-4 w-4 mr-1" />
                    <span className="text-xs">{post.likes}</span>
                  </div>
                  <div className="flex items-center text-gray-500">
                    <MessageCircle className="h-4 w-4 mr-1" />
                    <span className="text-xs">{post.comments}</span>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Categories Overview */}
      <div>
        <h2 className="text-2xl font-bold text-gray-900 mb-6">Content Categories</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
          {mockBlogData.categories.map((category) => (
            <div key={category.id} className="card p-6 text-center transition-transform hover:-translate-y-1 hover:shadow-lg">
              <div className={`w-12 h-12 mx-auto mb-3 rounded-lg flex items-center justify-center ${
                category.color === 'blue' ? 'bg-blue-100 text-blue-600' :
                category.color === 'green' ? 'bg-green-100 text-green-600' :
                category.color === 'purple' ? 'bg-purple-100 text-purple-600' :
                category.color === 'orange' ? 'bg-orange-100 text-orange-600' :
                'bg-red-100 text-red-600'
              }`}>
                <FileText className="h-6 w-6" />
              </div>
              <h3 className="font-semibold text-gray-900 dark:text-white mb-1">{category.name}</h3>
              <p className="text-2xl font-bold text-gray-600 dark:text-gray-300">{category.count}</p>
              <p className="text-xs text-gray-500">posts</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const renderCategories = () => (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold text-gray-900">All Categories</h2>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {mockBlogData.categories.map((category) => (
          <div key={category.id} className="card p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{category.name}</h3>
              <span className="text-2xl font-bold text-gray-600 dark:text-gray-300">{category.count}</span>
            </div>
            <p className="text-gray-600 mb-4">
              {category.id === 'accounting-tips' && 'Best practices and industry insights for accounting professionals'}
              {category.id === 'case-studies' && 'Success stories and implementation examples from real businesses'}
              {category.id === 'industry-news' && 'Updates on accounting regulations and industry trends'}
              {category.id === 'company-updates' && 'ACCTOO product updates and company announcements'}
              {category.id === 'tutorials' && 'How-to guides and educational content for learning'}
            </p>
            <button className="w-full bg-blue-600 text-white py-2 rounded-md hover:bg-blue-700">
              View Posts
            </button>
          </div>
        ))}
      </div>
    </div>
  );

  const renderCaseStudies = () => (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold text-gray-900">Case Studies</h2>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {filteredFeatured.filter(post => post.category === 'case-studies').map((post) => (
          <div key={post.id} className="card p-6 transition-transform hover:-translate-y-1 hover:shadow-lg">
            <div className="flex items-center mb-3">
              <TrendingUp className="h-5 w-5 text-green-600 mr-2" />
              <span className="text-sm font-medium text-green-600">Case Study</span>
            </div>
            <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">{post.title}</h3>
            <p className="text-gray-600 dark:text-gray-300 mb-4">{post.excerpt}</p>
            <div className="flex items-center justify-between text-sm text-gray-500 mb-4">
              <span>By {post.author}</span>
              <span>{new Date(post.publishDate).toLocaleDateString()}</span>
            </div>
            <div className="flex items-center space-x-4 text-sm text-gray-500">
              <span>👁️ {post.views} views</span>
              <span>❤️ {post.likes} likes</span>
              <span>💬 {post.comments} comments</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  const renderTutorials = () => (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold text-gray-900">Tutorials & How-To Guides</h2>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {filteredRecent.filter(post => post.category === 'tutorials').map((post) => (
          <div key={post.id} className="card p-6 transition-transform hover:-translate-y-1 hover:shadow-lg">
            <div className="flex items-center mb-3">
              <BookOpen className="h-5 w-5 text-blue-600 mr-2" />
              <span className="text-sm font-medium text-blue-600">Tutorial</span>
            </div>
            <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">{post.title}</h3>
            <p className="text-gray-600 dark:text-gray-300 mb-4">{post.excerpt}</p>
            <div className="flex items-center justify-between text-sm text-gray-500">
              <span>By {post.author}</span>
              <span>{post.readTime}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  const renderNews = () => (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold text-gray-900">Industry News & Updates</h2>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {filteredFeatured.filter(post => post.category === 'industry-news').map((post) => (
          <div key={post.id} className="card p-6 transition-transform hover:-translate-y-1 hover:shadow-lg">
            <div className="flex items-center mb-3">
              <FileText className="h-5 w-5 text-purple-600 mr-2" />
              <span className="text-sm font-medium text-purple-600">Industry News</span>
            </div>
            <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">{post.title}</h3>
            <p className="text-gray-600 dark:text-gray-300 mb-4">{post.excerpt}</p>
            <div className="flex items-center justify-between text-sm text-gray-500">
              <span>By {post.author}</span>
              <span>{new Date(post.publishDate).toLocaleDateString()}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  const renderAbout = () => (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold text-gray-900">About ACCTOO Blog</h2>
      <div className="bg-white rounded-lg shadow border p-6">
        <p className="text-gray-600 mb-4">
          The ACCTOO Blog is our content marketing and thought leadership platform designed to provide valuable insights 
          to accounting professionals, small business owners, and finance enthusiasts.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
          <div>
            <h3 className="font-semibold text-gray-900 mb-2">Our Mission</h3>
            <p className="text-sm text-gray-600">
              To educate, inform, and inspire the accounting community with practical insights, 
              industry trends, and success stories.
            </p>
          </div>
          <div>
            <h3 className="font-semibold text-gray-900 mb-2">Content Goals</h3>
            <p className="text-sm text-gray-600">
              Publish 3-5 high-quality posts per week covering accounting tips, case studies, 
              industry news, and educational content.
            </p>
          </div>
        </div>
      </div>
    </div>
  );

  const renderFAQ = () => (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Frequently Asked Questions</h2>
      <div className="space-y-3">
        {mockBlogData.faqs.map((item, index) => (
          <div key={index} className="card overflow-hidden">
            <button
              onClick={() => setFaqOpenIndex(faqOpenIndex === index ? -1 : index)}
              className="w-full flex items-center justify-between px-5 py-4 text-left"
            >
              <span className="font-medium text-gray-900 dark:text-white">{item.q}</span>
              <span className="ml-4 text-gray-500">{faqOpenIndex === index ? '−' : '+'}</span>
            </button>
            {faqOpenIndex === index && (
              <div className="px-5 pb-5 pt-0 text-sm text-gray-600 dark:text-gray-300 border-t">
                {item.a}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );

  const renderContent = () => {
    switch (activeTab) {
      case 'home':
        return renderHome();
      case 'categories':
        return renderCategories();
      case 'case-studies':
        return renderCaseStudies();
      case 'tutorials':
        return renderTutorials();
      case 'news':
        return renderNews();
      case 'faq':
        return renderFAQ();
      case 'about':
        return renderAbout();
      default:
        return renderHome();
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-gray-100 dark:from-slate-900 dark:to-slate-950">
      {/* Header */}
      <header className="bg-white/80 dark:bg-slate-900/70 backdrop-blur supports-[backdrop-filter]:bg-white/80 shadow-sm border-b sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <FileText className="h-8 w-8 text-blue-600 mr-3" />
              <h1 className="text-xl font-bold text-gray-900 dark:text-white">ACCTOO Blog</h1>
              <span className="ml-3 px-2 py-1 text-xs bg-green-100 text-green-800 rounded-full">Port 3002</span>
            </div>
            <div className="flex items-center space-x-4">
              <div className="relative">
                <Search className="h-5 w-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search posts..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white/90 dark:bg-slate-800 dark:border-slate-700 dark:text-white"
                />
              </div>
              <button onClick={() => setIsDark(v => !v)} className="hidden sm:inline-flex items-center px-3 py-2 rounded-md border border-gray-300 dark:border-slate-700 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-slate-800">
                {isDark ? 'Light' : 'Dark'}
              </button>
              <a href={process.env.NEXT_PUBLIC_PUBLIC_URL || 'https://acctoo.com/#pricing'} className="btn-primary">
                Get Started
              </a>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Navigation Tabs */}
        <nav className="flex space-x-8 mb-8">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center space-x-2 px-3 py-2 rounded-md text-sm font-medium ${
                  activeTab === tab.id
                    ? 'bg-blue-100 text-blue-700'
                    : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
                }`}
              >
                <Icon className="h-5 w-5" />
                <span>{tab.name}</span>
              </button>
            );
          })}
        </nav>

        {/* Content */}
        <main>
          {renderContent()}
        </main>
      </div>

      {/* Footer */}
      <footer className="border-t bg-white mt-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 grid grid-cols-1 md:grid-cols-4 gap-8 text-sm">
          <div>
            <div className="flex items-center mb-3">
              <FileText className="h-6 w-6 text-blue-600 mr-2" />
              <span className="font-semibold text-gray-900">ACCTOO Blog</span>
            </div>
            <p className="text-gray-600">Insights, tips, and stories for accounting professionals.</p>
          </div>
          <div>
            <h4 className="font-semibold text-gray-900 mb-2">Explore</h4>
            <ul className="space-y-1 text-gray-600">
              <li><button onClick={() => setActiveTab('categories')} className="hover:text-gray-900">Categories</button></li>
              <li><button onClick={() => setActiveTab('case-studies')} className="hover:text-gray-900">Case Studies</button></li>
              <li><button onClick={() => setActiveTab('tutorials')} className="hover:text-gray-900">Tutorials</button></li>
              <li><button onClick={() => setActiveTab('news')} className="hover:text-gray-900">Industry News</button></li>
              <li><button onClick={() => setActiveTab('faq')} className="hover:text-gray-900">FAQ</button></li>
            </ul>
          </div>
          <div>
            <h4 className="font-semibold text-gray-900 mb-2">Company</h4>
            <ul className="space-y-1 text-gray-600">
              <li><a href={process.env.NEXT_PUBLIC_PUBLIC_URL || 'https://acctoo.com'} className="hover:text-gray-900">Website</a></li>
              <li><button onClick={() => setActiveTab('about')} className="hover:text-gray-900">About</button></li>
            </ul>
          </div>
          <div>
            <h4 className="font-semibold text-gray-900 mb-2">Subscribe</h4>
            <p className="text-gray-600 mb-3">Get the latest posts in your inbox.</p>
            <div className="flex items-center space-x-2">
              <input type="email" placeholder="you@example.com" className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500" />
              <button className="btn-primary">Join</button>
            </div>
          </div>
        </div>
        <div className="border-t py-4 text-center text-xs text-gray-500">© {new Date().getFullYear()} ACCTOO. All rights reserved.</div>
      </footer>
    </div>
  );
};

export default App;
